import { api, extractErrorMessage, type ApiEnvelope } from './api';
import type { CertStatusCode } from './store';

/**
 * Typed wrappers untuk endpoint /api/onboarding/*.
 * Setiap function return data mentah dari ApiEnvelope.data (bukan envelope-nya).
 * Error dilempar sebagai Error dengan message yang ramah (parse AxiosError).
 */

export type SessionState = {
    sessionId: string | null;
    step: number;
    userType: string | null;
    phone: string | null;
    phoneVerified: boolean;
    registrationId: number | null;
    isAuthenticated: boolean;
};

function unwrap<T>(envelope: ApiEnvelope<T>): T {
    if (envelope.status !== 'ok' || envelope.data === undefined) {
        throw new Error(envelope.message ?? 'API error');
    }
    return envelope.data;
}

/** Wrap axios call → kalau fail parse response body jadi Error dengan pesan jelas. */
async function withErrorParsing<T>(fn: () => Promise<T>): Promise<T> {
    try {
        return await fn();
    } catch (e) {
        throw new Error(extractErrorMessage(e));
    }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
    return withErrorParsing(async () => {
        const res = await api.post<ApiEnvelope<T>>(`/api/onboarding${path}`, body);
        return unwrap(res.data);
    });
}

async function get<T>(path: string): Promise<T> {
    return withErrorParsing(async () => {
        const res = await api.get<ApiEnvelope<T>>(`/api/onboarding${path}`);
        return unwrap(res.data);
    });
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
    return withErrorParsing(async () => {
        const res = await api.post<ApiEnvelope<T>>(`/api/onboarding${path}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return unwrap(res.data);
    });
}

// ─── Session ──────────────────────────────────────────────────────

export function initSession() {
    return post<{ sessionId: string; step: number }>('/session/init');
}

export function getSession() {
    return get<SessionState>('/session');
}

// ─── User Type ────────────────────────────────────────────────────

export function setUserType(userType: 'INDIVIDUAL' | 'CORPORATE') {
    return post<{ userType: string; step: number }>('/user-type', { userType });
}

// ─── Phone + OTP ──────────────────────────────────────────────────

export function setPhone(phone: string, countryCode = '+62') {
    return post<{ phone: string; step: number }>('/phone', { phone, countryCode });
}

export function sendPhoneOtp() {
    return post<{ sent: boolean; expiresIn: number; devOtp?: string | null }>(
        '/phone/otp-send'
    );
}

export function verifyPhoneOtp(otp: string) {
    return post<{ phoneVerified: boolean; step: number }>('/phone/otp-verify', { otp });
}

// ─── Data Form ────────────────────────────────────────────────────

export type DataFormResult = {
    registrationId: number;
    status: CertStatusCode;
    nextStep: string;
    isAuthenticated: boolean;
};

export type DataFormPayload = {
    nik: string;
    fullName: string;
    email: string;
    password: string;
    password_confirmation: string;
    dateOfBirth: string;        // YYYY-MM-DD
    placeOfBirth: string;
    gender: 'M' | 'F';
    address: string;
    city: string;
    province: string;
    ktpPhotoUrl: string;        // URL hasil direct-upload ke MinIO
    npwpPhotoUrl?: string;
    npwp?: string;
};

export function submitDataForm(payload: DataFormPayload) {
    return post<DataFormResult>('/data-form', payload);
}

// ─── Liveness ────────────────────────────────────────────────────

export type LivenessResult = {
    attemptNumber: number;
    attemptsLeft: number;
    resultCode: string;
    resultDesc: string;
    nextStep: string | null;
};

/**
 * Submit video liveness sebagai base64 string (bukan multipart upload).
 * Alasan: spec Peruri memang minta base64, dan payload JSON lebih konsisten
 * dengan endpoint lain (ktpPhotoUrl, dst.).
 */
export function submitLiveness(videoBase64: string, mimeType = 'video/webm') {
    return post<LivenessResult>('/liveness', {
        videoBase64,
        mimeType,
    });
}

// ─── Signature ───────────────────────────────────────────────────

export type SignatureResult = {
    specimenId: string | null;
    imageUrl: string | null;
    nextStep: string;
};

export function submitSignature(pngBlob: Blob) {
    const fd = new FormData();
    fd.append('signature', pngBlob, 'signature.png');
    return postForm<SignatureResult>('/signature', fd);
}

// ─── Status polling ──────────────────────────────────────────────

export type StatusResult = {
    registrationId: number;
    statusCode: CertStatusCode;
    resultCode: string;
    resultDesc: string;
    peruriData: unknown;
};

export function getStatus() {
    return get<StatusResult>('/status');
}

// ─── Helper: convert data URL PNG ke Blob ────────────────────────

export function dataUrlToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mimeMatch = header.match(/data:([^;]+);base64/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}
